/**
 * DeepRunProgress — what a deep run is doing right now, in the chat, for as
 * long as it runs — and again when you come back.
 *
 * A deep run lives on a research worker and outlives the page. This follows
 * its event log — `GET /ask/runs/:id/stream?after_seq=N`, which replays and
 * then follows — so a reload shows the same trail from the start and keeps
 * going, and a run that finished while you were away shows as finished with
 * its answer below. Open while it runs; folded to one line once it is done,
 * the way a quick answer's investigation trail is.
 *
 * The one decision here is Cancel. There is no plan to approve: extended
 * thinking answers the question, it does not propose to.
 */
import React, { useEffect, useRef, useState } from "react";
import { Loader2, Check, X, ChevronDown, ChevronRight } from "lucide-react";
import axiosInstance from "shared/services/api/axiosInstance";
import { trodoAnalyticsTheme as theme } from "features/analytics/studio/theme";
import { ChartBlock, KpiStrip } from "shared/components/chat/AnswerRenderer";

const PHASE_LABEL = {
  intake: "Understanding the question",
  recon: "Looking at your data",
  plan: "Deciding what to check",
  investigate: "Investigating",
  validate: "Checking every claim against the data",
  report: "Writing the answer",
};
const TERMINAL = new Set(["done", "failed", "cancelled"]);

function apiBase() {
  const explicit = (process.env.REACT_APP_API_SERVER_URL || "").trim();
  return explicit ? `${explicit}/api` : "/api";
}

/**
 * An investigator's claim carries figure references — "{{ds_1.matched|count}}
 * runs" — that only the report resolves against the data. In a progress line
 * the reference is noise; the sentence around it is the news.
 */
export function plainClaim(text) {
  return String(text || "")
    .replace(/\{\{\s*[^}]*\}\}/g, "…")
    // A summary cut to length upstream can end mid-reference.
    .replace(/\{\{[^}]*$/, "…")
    .replace(/\s*…\s*(?=…)/g, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

/**
 * Fold one run event into the timeline. Pure, so a replay and a live feed
 * produce the same state and the tests can drive it without a socket.
 */
export function foldEvent(state, type, payload = {}) {
  const s = { ...state, lines: state.lines.slice(), investigators: { ...state.investigators }, findings: (state.findings || []).slice() };
  const push = (id, text, status = "info") => {
    const idx = s.lines.findIndex((l) => l.id === id);
    const line = { id, text, status };
    if (idx >= 0) s.lines[idx] = { ...s.lines[idx], ...line };
    else s.lines.push(line);
  };
  // A run that moves on settles whatever was still spinning: once it is back
  // in the queue, "Waiting for you to approve the plan" is history.
  const settle = () => { for (const l of s.lines) if (l.status === "running") l.status = "ok"; };
  switch (type) {
    case "status": {
      const st = payload.status;
      // The queue is announced once: the conductor and the enqueue both emit
      // `queued`, and the replay showed the line twice.
      if (st === "queued") { settle(); s.status = "queued"; push(payload.resumed_from ? `status:${s.lines.length}` : "status:queued", payload.resumed_from ? "Back in the queue: a research worker picks it up in a moment" : "Queued: a research worker picks this up in a moment", "running"); }
      else if (st === "running") { settle(); s.status = "running"; push(`status:${s.lines.length}`, "A research worker has it", "ok"); }
      else if (st === "resuming") { settle(); s.status = "running"; push(`status:${s.lines.length}`, `Picked up again${payload.from_phase ? ` after ${PHASE_LABEL[payload.from_phase]?.toLowerCase() || payload.from_phase}` : ""}`, "ok"); }
      else if (st === "plan_gate") { s.status = "plan_gate"; push("gate", "Waiting for you to approve the plan", "running"); }
      else if (st === "clarifying") { s.status = "clarifying"; push("gate", "Waiting for your answer", "running"); }
      else if (TERMINAL.has(st)) { s.status = st; }
      else if (st) { s.status = st; }
      break;
    }
    case "phase":
      if (payload.status === "start") { settle(); s.phase = payload.phase; push(`phase:${payload.phase}`, `${PHASE_LABEL[payload.phase] || payload.phase}…`, "running"); }
      else if (payload.status === "done") push(`phase:${payload.phase}`, PHASE_LABEL[payload.phase] || payload.phase, "ok");
      break;
    case "recon":
      push("recon", `Looked at your data${payload.runs != null ? `: ${payload.runs} runs` : ""}${payload.ms ? ` in ${Math.round(payload.ms / 1000)}s` : ""}`, "ok");
      break;
    case "plan_gate":
      // History only: runs from before the gate was removed.
      if (!payload.auto_approved) { s.status = "plan_gate"; push("gate", "Waiting for you to approve the plan", "running"); }
      break;
    case "plan_ready":
      settle();
      push("phase:plan", PHASE_LABEL.plan, "ok");
      break;
    case "wave":
      if (payload.status === "start") { s.wave = payload.wave; push(`wave:${payload.wave}`, `Wave ${payload.wave}${payload.reasoning ? `: ${String(payload.reasoning).slice(0, 140)}` : ""}`, "running"); }
      else if (payload.status === "done" || payload.status === "concluded") push(`wave:${payload.wave}`, `Wave ${payload.wave}${payload.summary ? `: ${plainClaim(payload.summary).slice(0, 140)}` : " — done"}`, "ok");
      break;
    case "investigator": {
      const id = `inv:${payload.wave ?? 0}:${payload.task_id || ""}`;
      const name = String(payload.task_id || "").replace(/_/g, " ");
      if (payload.status === "start") { s.investigators[id] = "running"; push(id, `Investigating: ${String(payload.objective || name).slice(0, 140)}`, "running"); }
      else if (payload.status === "done") {
        s.investigators[id] = "ok";
        push(id, `${name}: ${plainClaim(payload.claim || "done").slice(0, 120)}`, "ok");
        // What it found, in full and with its chart, for as long as the run
        // is still writing the answer.
        if (payload.claim && !s.findings.some((f) => f.id === id)) s.findings.push({ id, claim: plainClaim(payload.claim), exhibit: payload.exhibit || null });
      }
      else { s.investigators[id] = "error"; push(id, `${name}: could not be completed: the rest continues`, "error"); }
      break;
    }
    case "plan_update":
      push(`plan_update:${s.lines.length}`, `Plan revised (${payload.changes} change${payload.changes === 1 ? "" : "s"})`, "ok");
      break;
    case "validate":
      push("validate", `Checking ${payload.claims ?? ""} claims against the data…`, "running");
      break;
    case "answer":
      push("validate", "Claims checked", "ok");
      push("report", "Answer written: it appears below", "ok");
      break;
    case "done":
      s.status = payload.status || "done";
      if (s.status === "done") push("report", "Answer written: it appears below", "ok");
      else if (s.status === "failed") push("report", payload.error ? `Failed: ${String(payload.error).slice(0, 160)}` : "The research failed", "error");
      else if (s.status === "cancelled") push("report", "Cancelled", "error");
      break;
    default:
      break;
  }
  return s;
}

export const EMPTY = { status: null, phase: null, wave: null, lines: [], investigators: {}, findings: [] };

/**
 * Follow a run's event stream with fetch (EventSource cannot carry the
 * bearer token). Replays from 0, then follows; reconnects from the last seq.
 */
function useRunStream(runId, teamId, onTerminal) {
  const [state, setState] = useState(EMPTY);
  // null until the stream first opens: "connecting", not "reconnecting".
  const [connected, setConnected] = useState(null);
  const lastSeq = useRef(0);
  const stateRef = useRef(EMPTY);
  const terminalRef = useRef(false);
  // The callback is read through a ref so a parent that re-renders with a
  // fresh arrow does not tear the socket down and open it again — every
  // re-render of the chat page was doing exactly that.
  const onTerminalRef = useRef(onTerminal);
  onTerminalRef.current = onTerminal;

  useEffect(() => {
    if (!runId) return undefined;
    let cancelled = false;
    let controller = null;
    let retry = 0;

    const apply = (type, payload) => {
      stateRef.current = foldEvent(stateRef.current, type, payload);
      setState(stateRef.current);
      if (!terminalRef.current && TERMINAL.has(stateRef.current.status)) {
        terminalRef.current = true;
        try { onTerminalRef.current?.(stateRef.current.status); } catch { /* a viewer never throws */ }
      }
    };

    const connect = async () => {
      if (cancelled) return;
      controller = new AbortController();
      const token = localStorage.getItem("accessToken") || localStorage.getItem("token");
      const headers = { Accept: "text/event-stream" };
      if (token) headers.Authorization = `Bearer ${token}`;
      if (teamId != null) headers["X-Team-Id"] = String(teamId);
      try {
        const res = await fetch(`${apiBase()}/ask/runs/${encodeURIComponent(runId)}/stream?after_seq=${lastSeq.current}&team_id=${encodeURIComponent(teamId ?? "")}`, { headers, signal: controller.signal });
        if (!res.ok || !res.body) throw new Error(`stream ${res.status}`);
        setConnected(true);
        retry = 0;
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let event = null;
        let id = null;
        let data = "";
        for (;;) {
          const { done, value } = await reader.read();
          if (done || cancelled) break;
          buffer += decoder.decode(value, { stream: true });
          let nl;
          // eslint-disable-next-line no-cond-assign
          while ((nl = buffer.indexOf("\n")) >= 0) {
            const line = buffer.slice(0, nl);
            buffer = buffer.slice(nl + 1);
            if (line === "") {
              if (event) {
                let payload = {};
                try { payload = data ? JSON.parse(data) : {}; } catch { payload = {}; }
                if (id) lastSeq.current = Math.max(lastSeq.current, Number(id) || 0);
                apply(event, payload);
              }
              event = null; id = null; data = "";
            } else if (line.startsWith("event:")) event = line.slice(6).trim();
            else if (line.startsWith("id:")) id = line.slice(3).trim();
            else if (line.startsWith("data:")) data += line.slice(5).trim();
          }
        }
      } catch (e) {
        if (cancelled || e?.name === "AbortError") return;
      }
      setConnected(false);
      // The server closes a finished run's stream itself; only an unfinished
      // one is worth reconnecting to.
      if (!cancelled && !terminalRef.current) {
        retry += 1;
        setTimeout(connect, Math.min(15000, 1000 * 2 ** Math.min(retry, 4)));
      }
    };
    connect();
    return () => { cancelled = true; controller?.abort(); };
  }, [runId, teamId]);

  return { state, connected };
}

function Icon({ status }) {
  if (status === "running") return <Loader2 size={12} className="animate-spin" style={{ color: theme.primary }} />;
  if (status === "ok") return <Check size={12} style={{ color: theme.textSecondary }} />;
  if (status === "error") return <X size={12} style={{ color: "var(--feedback-error)" }} />;
  return <span style={{ width: 12, display: "inline-block" }} />;
}

/**
 * @param {string} props.runId
 * @param {number} props.teamId
 * @param {function} [props.onFinished]  called once when the run reaches a
 *   terminal state — the chat reloads its messages so the delivered report
 *   appears without a manual refresh.
 */
export default function DeepRunProgress({ runId, teamId, onFinished }) {
  const [initial, setInitial] = useState(null); // the run's status before any event arrives
  // null = not chosen by the reader: open while it runs, folded once done.
  const [openChoice, setOpenChoice] = useState(null);
  const [stopping, setStopping] = useState(false);
  const [stopError, setStopError] = useState(null);

  // One status check first: a run that finished an hour ago needs no socket,
  // and its stream would close after the replay anyway.
  useEffect(() => {
    let cancelled = false;
    axiosInstance.get(`/ask/runs/${runId}`, { params: { team_id: teamId } })
      .then(({ data }) => { if (!cancelled) setInitial(data?.run?.status || "unknown"); })
      .catch(() => { if (!cancelled) setInitial("unknown"); });
    return () => { cancelled = true; };
  }, [runId, teamId]);

  const finishedRef = useRef(false);
  const onTerminal = (status) => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    onFinished?.(status);
  };

  const { state, connected } = useRunStream(initial ? runId : null, teamId, onTerminal);

  const stop = async (e) => {
    e.stopPropagation();
    setStopping(true);
    setStopError(null);
    try {
      await axiosInstance.post(`/ask/runs/${runId}/cancel`, { team_id: teamId }, { params: { team_id: teamId } });
    } catch (err) {
      if (err?.response?.status !== 409) setStopError(err?.response?.data?.error || "Could not stop the run: try again.");
    } finally {
      setStopping(false);
    }
  };

  if (!initial) return null;
  const status = state.status || initial;
  const live = !TERMINAL.has(status);
  const open = openChoice == null ? live : openChoice;
  const lines = state.lines;
  const investigating = Object.values(state.investigators).filter((v) => v === "running").length;
  const doneInv = Object.values(state.investigators).filter((v) => v === "ok").length;
  const totalInv = Object.keys(state.investigators).length;

  let summary;
  if (status === "done") summary = `Research trail${totalInv ? ` · ${totalInv} line${totalInv === 1 ? "" : "s"} of investigation` : ""}`;
  else if (status === "failed") summary = "Research failed";
  else if (status === "cancelled") summary = "Research cancelled";
  else if (status === "plan_gate") summary = "Waiting for you to approve the plan";
  // A run whose phases have started is working, whatever the last status
  // event said — the worker announces phases, not a second "running".
  else if (status === "queued" && !state.phase) summary = "Queued: a research worker picks this up in a moment";
  else if (totalInv) summary = `${PHASE_LABEL[state.phase] || "Investigating"} · wave ${state.wave ?? 1} · ${doneInv}/${totalInv} lines answered${investigating ? `, ${investigating} running` : ""}`;
  else summary = `${PHASE_LABEL[state.phase] || "Working"}…`;

  return (
    <div className="rounded-xl border w-full min-w-0 mt-2" data-testid="deep-run-progress" data-status={status}
      style={{ borderColor: theme.border, backgroundColor: theme.bgRaised || "rgba(0,0,0,0.015)" }}>
      <div className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium select-none" style={{ color: theme.textSecondary }}>
        <button type="button" onClick={() => setOpenChoice(!open)} aria-expanded={open}
          className="flex items-center gap-2 min-w-0 flex-1 text-left">
          {live ? <Loader2 size={12} className="animate-spin shrink-0" style={{ color: theme.primary }} /> : status === "done" ? <Check size={12} className="shrink-0" /> : <X size={12} className="shrink-0" style={{ color: "var(--feedback-error)" }} />}
          <span className="truncate">{summary}</span>
          {live && !connected && <span className="text-[10px]" style={{ color: theme.textMuted || theme.textSecondary }}>{connected === null ? "· connecting" : "· reconnecting"}</span>}
        </button>
        <span className="flex items-center gap-2 shrink-0">
          {live && (
            <button type="button" onClick={stop} disabled={stopping}
              className="px-2 py-0.5 rounded-md text-[11px] font-medium border transition-colors disabled:opacity-60"
              style={{ borderColor: theme.border, color: theme.textPrimary, backgroundColor: "transparent" }}>
              {stopping ? "Stopping…" : "Cancel"}
            </button>
          )}
          {stopError && <span style={{ color: "var(--feedback-error)" }}>{stopError}</span>}
          <button type="button" onClick={() => setOpenChoice(!open)} aria-label={open ? "Fold the trail" : "Show the trail"}>
            {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          </button>
        </span>
      </div>
      {open && lines.length > 0 && (
        <ul className="px-3 pb-2 grid gap-1" style={{ fontSize: 12, lineHeight: 1.45, color: theme.textSecondary }}>
          {lines.map((l) => (
            <li key={l.id} className="flex items-start gap-2 min-w-0">
              <span className="mt-[3px] shrink-0"><Icon status={l.status} /></span>
              <span className="min-w-0 break-words" style={{ color: l.status === "running" ? theme.textPrimary : theme.textSecondary }}>{l.text}</span>
            </li>
          ))}
        </ul>
      )}
      {/* Findings as they land. The answer below replaces them when it is written. */}
      {live && state.findings.length > 0 && (
        <div className="px-3 pb-3 pt-1 grid gap-3 border-t" style={{ borderColor: theme.border }}>
          {state.findings.map((f) => (
            <div key={f.id} className="min-w-0">
              <div style={{ fontSize: 13, color: theme.textPrimary, lineHeight: 1.5 }}>{f.claim}</div>
              {f.exhibit?.kind === "chart" && <ChartBlock block={f.exhibit} />}
              {f.exhibit?.kind === "kpis" && <KpiStrip block={f.exhibit} />}
            </div>
          ))}
        </div>
      )}
      {open && lines.length === 0 && live && (
        <div className="px-3 pb-2" style={{ fontSize: 12, color: theme.textSecondary }}>
          {status === "queued" ? "It keeps running if you leave this page; the answer is delivered here when it finishes." : "Starting…"}
        </div>
      )}
    </div>
  );
}
